import { computed, ref } from 'vue';
import SurveyFormsAPI from 'dashboard/api/surveyForms';
import SurveyResponsesAPI from 'dashboard/api/surveyResponses';
import { RESPONSES_PER_PAGE } from '../constants';
import { normalizeForm } from '../helper/surveyFormHelper';
import {
  normalizeMetrics,
  normalizeResponse,
  periodRange,
} from '../helper/surveyResponseHelper';
import {
  apiErrorMessage,
  extractCollection,
  extractRecord,
} from './useSurveyForms';

/**
 * Responses + metrics of a single survey form.
 *
 * Both requests share the same period/status filters so the strip on top and
 * the table below can never disagree about which slice is on screen.
 */
export function useSurveyResponses(formId) {
  const form = ref(null);
  const responses = ref([]);
  const metrics = ref(normalizeMetrics({}));
  const total = ref(0);
  const page = ref(1);

  const filters = ref({ status: '', periodDays: 30 });

  const isLoadingForm = ref(false);
  const isLoadingResponses = ref(false);
  const isLoadingMetrics = ref(false);
  const isDownloading = ref(false);
  const hasError = ref(false);
  const errorMessage = ref('');

  const questions = computed(() => form.value?.questions || []);
  const totalPages = computed(() =>
    Math.max(1, Math.ceil((total.value || 0) / RESPONSES_PER_PAGE))
  );
  const isEmpty = computed(
    () => !isLoadingResponses.value && responses.value.length === 0
  );
  const hasFilters = computed(
    () => Boolean(filters.value.status) || filters.value.periodDays !== 0
  );

  const filterParams = () => {
    const params = {
      ...periodRange(filters.value.periodDays),
    };
    if (filters.value.status) params.status = filters.value.status;
    return params;
  };

  const requestParams = () => ({ page: page.value, ...filterParams() });

  const fetchForm = async () => {
    isLoadingForm.value = true;
    try {
      const { data } = await SurveyFormsAPI.show(formId);
      form.value = normalizeForm(extractRecord(data));
      return { ok: true };
    } catch (error) {
      hasError.value = true;
      errorMessage.value = apiErrorMessage(error);
      return { ok: false };
    } finally {
      isLoadingForm.value = false;
    }
  };

  const fetchResponses = async () => {
    isLoadingResponses.value = true;
    try {
      const { data } = await SurveyFormsAPI.responses(formId, requestParams());
      responses.value = extractCollection(data).map(normalizeResponse);
      total.value = Number(data?.meta?.count) || responses.value.length;
      return { ok: true };
    } catch (error) {
      hasError.value = true;
      errorMessage.value = apiErrorMessage(error);
      return { ok: false };
    } finally {
      isLoadingResponses.value = false;
    }
  };

  const fetchMetrics = async () => {
    isLoadingMetrics.value = true;
    try {
      const params = {
        survey_form_id: formId,
        ...filterParams(),
      };
      const { data } = await SurveyResponsesAPI.metrics(params);
      metrics.value = normalizeMetrics(extractRecord(data));
      return { ok: true };
    } catch (error) {
      // Metrics are complementary: the table stays usable without them.
      metrics.value = normalizeMetrics({});
      return { ok: false, message: apiErrorMessage(error) };
    } finally {
      isLoadingMetrics.value = false;
    }
  };

  const fetchResponse = async responseId => {
    try {
      const { data } = await SurveyResponsesAPI.show(responseId);
      return normalizeResponse(extractRecord(data));
    } catch {
      return null;
    }
  };

  const downloadCsv = async () => {
    if (isDownloading.value) return { ok: false, duplicate: true };

    isDownloading.value = true;
    try {
      const response = await SurveyResponsesAPI.download({
        survey_form_id: formId,
        ...filterParams(),
      });
      return {
        ok: true,
        blob: response.data,
        contentDisposition: response.headers?.['content-disposition'] || '',
      };
    } catch (error) {
      return { ok: false, message: apiErrorMessage(error) };
    } finally {
      isDownloading.value = false;
    }
  };

  const refresh = async () => {
    hasError.value = false;
    errorMessage.value = '';
    await Promise.all([fetchResponses(), fetchMetrics()]);
  };

  const load = async () => {
    hasError.value = false;
    errorMessage.value = '';
    await fetchForm();
    await refresh();
  };

  const setFilter = async (key, value) => {
    filters.value = { ...filters.value, [key]: value };
    page.value = 1;
    await refresh();
  };

  const goToPage = async nextPage => {
    const target = Math.min(Math.max(nextPage, 1), totalPages.value);
    if (target === page.value) return;
    page.value = target;
    await fetchResponses();
  };

  return {
    form,
    questions,
    responses,
    metrics,
    total,
    totalPages,
    page,
    filters,
    hasFilters,
    isLoadingForm,
    isLoadingResponses,
    isLoadingMetrics,
    isDownloading,
    isEmpty,
    hasError,
    errorMessage,
    load,
    fetchResponse,
    downloadCsv,
    refresh,
    setFilter,
    goToPage,
  };
}

export default useSurveyResponses;
